import { useEffect, useRef, useState } from 'react'
import questionService from '@/api/question'
import useCryptoLivePrice from '@/hooks/useCryptoLivePrice'
import { getCryptoClosePrice, getCryptoPairSymbol, isCryptoMarketSettled } from '@/features/market/utils/cryptoEvent'

/* The stream ticks every second; repainting a screen of cards that often is noise. */
const REPAINT_MS = 2000

/** Lets a value through at most once per window, the newest one winning; the first one, and a reset to null, at once. */
const useThrottledValue = (value, delay) => {
  const [shown, setShown] = useState(value)
  const appliedAt = useRef(0)

  useEffect(() => {
    const wait = value === null || !appliedAt.current ? 0 : Math.max(0, appliedAt.current + delay - Date.now())
    const timer = setTimeout(() => {
      appliedAt.current = Date.now()
      setShown(value)
    }, wait)
    return () => clearTimeout(timer)
  }, [value, delay])

  return shown
}

/**
 * The price a crypto price market is measured against right now: the recorded close once
 * the backend has one, otherwise the live price from the shared Binance stream, throttled
 * for display. Null until the first tick, when the market has no pair, or once its window has
 * closed without a recorded close (settled, or waiting on resolution): the price now does not
 * decide it, so a live figure would mislead.
 */
const useCryptoCurrentPrice = question => {
  const closePrice = getCryptoClosePrice(question)
  const isLive = !isCryptoMarketSettled(question) && !questionService.isWaitingForResults(question)
  const livePrice = useCryptoLivePrice(closePrice === null && isLive ? getCryptoPairSymbol(question) : null)
  const shownLivePrice = useThrottledValue(livePrice, REPAINT_MS)
  return closePrice ?? shownLivePrice
}

export default useCryptoCurrentPrice
